/**
 * useLiteAssessment — all state, effects and handlers for the public lite
 * self-assessment (load config, resume draft, debounced autosave, scoring,
 * lead submit). Extracted verbatim from LiteAssessment.jsx.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { setSnapshot } from "../../lib/maturitySnapshots";
import { getDeviceId } from "../../lib/deviceId";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

export default function useLiteAssessment() {
  const { slug } = useParams();
  const realSlug = (slug || "").replace(/-self-assessment$/, "");
  const [lite, setLite] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [phase, setPhase] = useState("intro"); // intro | questions | result | submitted
  const [submitting, setSubmitting] = useState(false);
  const [lead, setLead] = useState({ full_name: "", email: "", organisation: "", role: "", sector: "", consent: false });

  useEffect(() => {
    axios.get(`${API_BASE}/public/assessments/lite/${realSlug}`)
      .then((r) => setLite(r.data))
      .catch(() => toast.error("Self-assessment not found"));
  }, [realSlug]);

  // Resume any in-progress draft for this device + slug from the server.
  const [resumedFromDraft, setResumedFromDraft] = useState(false);
  useEffect(() => {
    const did = getDeviceId();
    if (!did) return;
    let cancelled = false;
    axios
      .get(`${API_BASE}/public/assessments/lite/${realSlug}/resume`, { params: { device_id: did } })
      .then(({ data }) => {
        if (cancelled || !data?.answers) return;
        const count = Object.keys(data.answers).length;
        if (count === 0) return;
        setAnswers(data.answers);
        setResumedFromDraft(true);
        setPhase("questions");
        toast.success(`Resumed your draft — ${count} of ${data.total_questions || "?"} answered.`);
      })
      .catch(() => { /* no draft, expected */ });
    return () => { cancelled = true; };
  }, [realSlug]);

  // Debounced auto-save of partial answers to the server.
  const saveTimer = useRef(null);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | failed
  useEffect(() => {
    if (phase !== "questions") return;
    if (Object.keys(answers).length === 0) return;
    const did = getDeviceId();
    if (!did) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      setSaveState("saving");
      const startedAt = Date.now();
      const minVisibleMs = 450;
      axios
        .post(`${API_BASE}/public/assessments/lite/${realSlug}/progress`, {
          device_id: did,
          answers,
          current_index: Object.keys(answers).length - 1,
        })
        .then(() => {
          const elapsed = Date.now() - startedAt;
          const delay = Math.max(0, minVisibleMs - elapsed);
          setTimeout(() => setSaveState("saved"), delay);
          try {
            const answered = Object.keys(answers).length;
            const total = lite?.questions?.length || 0;
            if (total && answered > 0 && answered < total) {
              localStorage.setItem("ekselens.lite.activeDraft", JSON.stringify({
                routeSlug: slug,
                subBrand: lite?.sub_brand || lite?.title || "your",
                answered,
                total,
                updated_at: new Date().toISOString(),
              }));
            }
          } catch {/*noop*/}
        })
        .catch(() => setSaveState("failed"));
    }, 600);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [answers, phase, realSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  const completeness = useMemo(() => {
    if (!lite) return 0;
    return Math.round(100 * Object.keys(answers).length / lite.questions.length);
  }, [answers, lite]);

  const computeAndShow = async () => {
    try {
      const { data } = await axios.post(`${API_BASE}/public/assessments/lite/${realSlug}/score`, {
        answers,
        device_id: getDeviceId(),
      });
      setResult(data);
      setSnapshot(realSlug, data);
      try { localStorage.removeItem("ekselens.lite.activeDraft"); } catch {/*noop*/}
      setPhase("result");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Could not score the assessment. Please try again.");
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.consent) return toast.error("Please tick the consent box to proceed.");
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/public/assessments/lite/${realSlug}/submit`, { answers, ...lead });
      setPhase("submitted");
      toast.success("Thanks — we'll be in touch.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submit failed — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const radarSeries = result?.by_dimension?.length
    ? [{ name: "Your score", colour: "#7737F6", dimensions: result.by_dimension.map((d) => ({ dim: d.label, score: d.score })) }]
    : [];

  return {
    slug, realSlug, lite, answers, setAnswers, result, phase, setPhase,
    submitting, lead, setLead, resumedFromDraft, saveState, completeness,
    computeAndShow, submitLead, radarSeries,
  };
}
